import React, { useCallback, useMemo } from "react";
import { useAgentsStore } from "../../../../../../../../store/agents.store";
import {
    selectAgentDetailDraft,
    selectAllStates,
    selectAllStatesLoading,
    selectHiddenFields,
    selectIsEditMode,
    selectRestrictedFields,
} from "../../../../../../../../store/agentDetail/agentDetailSelectors";
import { isFieldEditable } from "../../../../../../../../store/agentDetail/agentDetail.utils";
import { buildStateSelectOptions } from "../../../../../../../../agents.utils";
import { AgentDetailSectionEnum, IAgentBusinessInformation } from "../../../../../../../../agentDetail.model";
import { AgentDetailFormField } from "../agentDetailFormField/agentDetailFormField.index";
import { BUSINESS_FORM_FIELDS } from "./businessPanel.constants";
import styles from "./businessPanel.module.css";

export const BusinessPanel = () => {
    const draft = useAgentsStore(selectAgentDetailDraft);
    const isEditMode = useAgentsStore(selectIsEditMode);
    const restrictedFields = useAgentsStore(selectRestrictedFields);
    const hiddenFields = useAgentsStore(selectHiddenFields);
    const allStates = useAgentsStore(selectAllStates);
    const allStatesLoading = useAgentsStore(selectAllStatesLoading);
    const stateOptions = useMemo(() => buildStateSelectOptions(allStates), [allStates]);
    const updateDraftField = useAgentsStore((state) => state.updateDraftField);

    const businessInformation = draft?.businessInformation;

    const isFieldHidden = useCallback(
        (field: keyof IAgentBusinessInformation) =>
            hiddenFields.businessInformation.includes(field),
        [hiddenFields.businessInformation]
    );

    const isFieldDisabled = useCallback(
        (field: keyof IAgentBusinessInformation) =>
            !isEditMode ||
            !isFieldEditable(restrictedFields, AgentDetailSectionEnum.BUSINESS_INFORMATION, field),
        [isEditMode, restrictedFields]
    );

    const handleFieldChange = useCallback(
        (field: keyof IAgentBusinessInformation, value: string | null) => {
            updateDraftField(AgentDetailSectionEnum.BUSINESS_INFORMATION, field, value);
        },
        [updateDraftField]
    );

    const formFields = useMemo(
        () =>
            BUSINESS_FORM_FIELDS.filter((fieldConfig) => !isFieldHidden(fieldConfig.field)).map(
                (fieldConfig) =>
                    fieldConfig.type === "select"
                        ? { ...fieldConfig, options: stateOptions, loading: allStatesLoading }
                        : fieldConfig
            ),
        [isFieldHidden, stateOptions, allStatesLoading]
    );

    return (
        <div className={styles.formGrid}>
            {formFields.map((fieldConfig) => {
                const disabled = isFieldDisabled(fieldConfig.field);
                return (
                    <AgentDetailFormField
                        key={fieldConfig.field}
                        config={fieldConfig}
                        value={(businessInformation?.[fieldConfig.field] as string | null) ?? null}
                        disabled={disabled}
                        onChange={
                            disabled
                                ? undefined
                                : (value) => handleFieldChange(fieldConfig.field, value)
                        }
                    />
                );
            })}
        </div>
    );
};
